import React from 'react';
import { X, Shield, Star } from 'lucide-react';
import { FormData } from '../App';

interface Submission extends FormData {
  id: string;
  created_at: string;
  total_cost: number;
  total_duration: number;
}

interface Props {
  submission: Submission;
  onClose: () => void;
}

function SubmissionDetails({ submission, onClose }: Props) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Calculation Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">State</label>
              <p className="mt-1 text-lg">{submission.state}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">District</label>
              <p className="mt-1 text-lg">{submission.district}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Cropping Pattern</label>
              <p className="mt-1 text-lg">{submission.croppingPattern}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Date</label>
              <p className="mt-1 text-lg">{new Date(submission.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Performance Ratings */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Ratings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-brand-blue" />
                  <label className="text-sm font-medium text-gray-700">Crop Safety Rating</label>
                </div>
                <div className="flex items-center">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 bg-brand-blue rounded-full"
                      style={{ width: `${(submission.cropSafetyRating / 10) * 100}%` }}
                    ></div>
                  </div>
                  <span className="ml-3 font-semibold">{submission.cropSafetyRating}/10</span>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-brand-orange" />
                  <label className="text-sm font-medium text-gray-700">Herbicide Efficacy Rating</label>
                </div>
                <div className="flex items-center">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 bg-brand-orange rounded-full"
                      style={{ width: `${(submission.herbicideEfficacyRating / 10) * 100}%` }}
                    ></div>
                  </div>
                  <span className="ml-3 font-semibold">{submission.herbicideEfficacyRating}/10 </span>
                </div>
              </div>
            </div>
          </div>

          {/* Herbicide Applications */}
          {submission.herbicides.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Herbicide Applications</h3>
              <div className="space-y-4">
                {submission.herbicides.map((herb, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Application #{index + 1} ({herb.type})</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Products</label>
                        <div className="mt-1 space-y-2">
                          {herb.products.map((product, pIndex) => (
                            <div key={pIndex} className="flex justify-between">
                              <span>{product.productName}</span>
                              <span>₹{product.cost}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="mb-2">
                          <label className="block text-sm font-medium text-gray-500">Spray Man Cost</label>
                          <p className="mt-1">₹{herb.sprayManCost}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Control Duration</label>
                          <p className="mt-1">{herb.controlDuration} days</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Manual Weeding Details */}
          {submission.manualWeedingDetails.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Manual Weeding Operations</h3>
              <div className="space-y-4">
                {submission.manualWeedingDetails.map((detail, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Operation #{index + 1}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Labour Required / Acre</label>
                        <p className="mt-1">{detail.laborers}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Cost per Laborer</label>
                        <p className="mt-1">₹{detail.costPerLaborer}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Control Duration</label>
                        <p className="mt-1">{detail.controlDuration} days</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Total Cost</label>
                <p className="mt-1 text-xl font-semibold text-green-600">₹{submission.total_cost.toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Total Duration</label>
                <p className="mt-1 text-xl font-semibold">{submission.total_duration} days</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubmissionDetails;